/* eslint-disable prettier/prettier */
/* eslint-disable sonarjs/no-duplicate-string */
/* eslint-disable sonarjs/cognitive-complexity */
'use client';

import React, { useEffect, useRef, useState } from 'react';

import usePageLoaded from '@/helpers/hooks/usePageLoaded';
import { useAppSelector } from '@/store';
import { AddedToCartData, IdentifyData, StartedCheckoutData, ViewedProductData } from '@/types/Klaviyo';

import { useAuthContext } from '../Providers/AuthProvider';

type KlaviyoProps =
  | {
      type: 'profiles';
      data: IdentifyData;
      event?: void;
    }
  | {
      type: 'events';
      event: 'Viewed Product';
      data: ViewedProductData;
    }
  | {
      type: 'events';
      event: 'Added to Cart';
      data: AddedToCartData;
    }
  | {
      type: 'events';
      event: 'Started Checkout';
      data: StartedCheckoutData;
    };

const KlaviyoComponent: React.FC<KlaviyoProps> = klaviyoProps => {
  const { customer } = useAuthContext();
  const isPageLoaded = usePageLoaded();
  const [isKlaviyoReady, setIsKlaviyoReady] = useState(false);
  const hasTracked = useRef(false);

  /**
   * Check if Klaviyo is available and set up polling if needed.
   * This ensures events are tracked even if Klaviyo script loads slowly.
   *
   * - Polls every 100ms for up to 10 seconds
   * - Automatically triggers tracking once Klaviyo is available
   * - Logs warning if Klaviyo fails to load
   */
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const checkKlaviyoAvailability = () => {
      try {
        // Check if klaviyo is available globally
        if (
          typeof klaviyo !== 'undefined' &&
          klaviyo &&
          typeof klaviyo.track === 'function' &&
          typeof klaviyo.identify === 'function'
        ) {
          setIsKlaviyoReady(true);
          return true;
        }
        return false;
      } catch (e) {
        return false;
      }
    };

    // Check immediately
    if (checkKlaviyoAvailability()) return;

    // Poll for Klaviyo availability
    const pollInterval = setInterval(() => {
      if (checkKlaviyoAvailability()) {
        clearInterval(pollInterval);
      }
    }, 100); // Check every 100ms

    // Clean up after 10 seconds
    const timeout = setTimeout(() => {
      clearInterval(pollInterval);
      try {
        if (typeof klaviyo === 'undefined' || !klaviyo || typeof klaviyo.track !== 'function') {
          console.warn('Klaviyo script failed to load within 10 seconds');
        }
      } catch (e) {
        console.warn('Klaviyo script failed to load within 10 seconds');
      }
    }, 10000);

    return () => {
      clearInterval(pollInterval);
      clearTimeout(timeout);
    };
  }, []);

  /**
   * Execute tracking when both page and Klaviyo are ready.
   *
   * - Waits for page load and Klaviyo availability
   * - Prevents duplicate tracking with hasTracked ref
   * - Automatically filters out empty values from identify calls
   * - Handles both profile identification and event tracking
   */
  useEffect(() => {
    if (!isPageLoaded || !isKlaviyoReady || hasTracked.current) {
      return;
    }

    const executeTracking = async () => {
      try {
        if (klaviyoProps.type === 'profiles') {
          // Identify user with profile data
          const identifyData: IdentifyData = {
            email: klaviyoProps.data.email || customer?.email || '',
            first_name: klaviyoProps.data.first_name || customer?.firstName || '',
            last_name: klaviyoProps.data.last_name || customer?.lastName || '',
            ...klaviyoProps.data, // Spread any additional custom properties
          };

          // Remove empty values
          Object.keys(identifyData).forEach(key => {
            if (identifyData[key] === '' || identifyData[key] === undefined) {
              delete identifyData[key];
            }
          });

          // Only identify if we have at least an email
          if (identifyData.email) {
            await klaviyo.identify(identifyData);
            hasTracked.current = true;
          }
        } else if (klaviyoProps.type === 'events') {
          // Track event with properties
          await klaviyo.track(klaviyoProps.event, klaviyoProps.data);
          hasTracked.current = true;
        }
      } catch (error) {
        console.error('Klaviyo tracking error:', error);
      }
    };

    executeTracking();
  }, [klaviyoProps, customer, isPageLoaded, isKlaviyoReady]);

  return null;
};

const KlaviyoWrapper: React.FC<KlaviyoProps> = klaviyoProps => {
  const cookieConsent = useAppSelector(state => state.slices.appConfig.isCookieConsentRejected);

  if (cookieConsent !== true) return <KlaviyoComponent {...klaviyoProps} />;

  return null;
};

export default React.memo(KlaviyoWrapper);
